import type { Role } from "@dutyon/apis";
import { parseJwt, useAuth } from "@dutyon/apis";
import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";

export interface DecodedAuthToken {
  email: string;
  sub: string;
  role: Role;
  teamId?: string;
  companyId?: string;
  nurseId?: string;
  exp?: number;
  tokenType?: "access";
  [key: string]: unknown;
}

export interface AuthGuardProps {
  /**
   * 접근을 허용할 역할(Role) 목록입니다.
   * 지정하지 않으면 로그인(토큰 존재 여부)만 확인합니다.
   */
  allowedRoles?: Role[];

  /**
   * 로그인하지 않았거나 권한이 부족하여 리다이렉트할 경로입니다.
   * @default "/login"
   */
  fallbackPath?: string;

  /**
   * 토큰은 있으나 역할(Role)이 일치하지 않을 때 커스텀하게 처리를 수행할 수 있는 콜백 함수입니다.
   * 모달 팝업 알림 등을 직접 렌더링하고 싶을 때 유용합니다.
   * @param decodedToken 파싱된 JWT 토큰 페이로드
   * @param actions 권한 실패 시 수행할 수 있는 동작들 (예: 토큰 삭제, 리다이렉트 제어)
   */
  onUnauthorized?: (
    decodedToken: DecodedAuthToken | null,
    actions: { clearToken: () => void; reject: () => void },
  ) => void;

  /**
   * 로딩 상태 등 권한 분석 상태가 결정되지 않았을 때 표시할 예비 화면입니다.
   */
  fallbackElement?: React.ReactNode;
}

/**
 * 모노레포 통합 공용 인증 및 인가 라우트 가드(AuthGuard) 컴포넌트입니다.
 */
export function AuthGuard({
  allowedRoles,
  fallbackPath = "/login",
  onUnauthorized,
  fallbackElement = null,
}: AuthGuardProps) {
  const { token, clearToken, logout } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    // 1. 토큰이 존재하지 않는 경우 바로 거부
    if (!token) {
      void logout({ reason: "session-expired" });
      setIsAuthorized(false);
      return;
    }

    const decoded = parseJwt(token) as DecodedAuthToken | null;
    const isExpired = decoded?.exp ? decoded.exp * 1000 <= Date.now() : true;
    if (!decoded || isExpired || decoded.tokenType !== "access") {
      void logout({ reason: "session-expired" });
      setIsAuthorized(false);
      return;
    }

    // 2. 허용할 특정 역할 목록이 주어지지 않은 경우, 유효한 로그인 token이면 통과
    if (!allowedRoles || allowedRoles.length === 0) {
      setIsAuthorized(true);
      return;
    }

    const userRole = decoded?.role;

    // 3. 사용자의 역할이 허용된 목록에 포함되어 있는 경우 통과
    if (decoded && userRole && (allowedRoles as string[]).includes(userRole as string)) {
      setIsAuthorized(true);
    } else {
      // 4. 권한 불일치 에러 대응
      const handleReject = () => {
        setIsAuthorized(false);
      };

      if (onUnauthorized) {
        onUnauthorized(decoded, {
          clearToken,
          reject: handleReject,
        });
      } else {
        // 커스텀 핸들러가 없을 경우, 기본 동작으로 토큰을 유지 혹은 비우고 즉시 리다이렉션 처리
        setIsAuthorized(false);
      }
    }
  }, [token, allowedRoles, clearToken, logout, onUnauthorized]);

  // 권한 상태 판단 전 단계 (로딩 등)
  if (isAuthorized === null) {
    return fallbackElement;
  }

  // 권한 미달 시 타겟 경로로 리다이렉트
  if (!isAuthorized) {
    return <Navigate to={fallbackPath} replace />;
  }

  // 통과 시 자식 Route 컴포넌트들 정상 노출
  return <Outlet />;
}
